<!-- PointCloudView.vue -->
<script lang="ts">
import { defineComponent, computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MapComponent from '@/components/MapComponent.vue'
import RawMapNotesLayer from '@/components/MapNotesLayer.vue'
import { useMapStore } from '@/stores/mapStore'
import VectorSource from 'ol/source/Vector.js'
import FileListVue from '@/components/FileList.vue'
import WebGLPointsLayer from 'ol/layer/WebGLPoints'
import Positioning from 'ol/Overlay'
import GeoJSON from 'ol/format/GeoJSON'
import WKT from 'ol/format/WKT'
import { Style, Circle as CircleStyle, Fill } from 'ol/style'
import Overlay from 'ol/Overlay'
import Chart from 'chart.js/auto'
import Collection from 'ol/Collection.js'
import Feature from 'ol/Feature.js'
import Point from 'ol/geom/Point.js'
import LineString from 'ol/geom/LineString.js'
import { fromLonLat, toLonLat } from 'ol/proj.js'
import { Polygon } from 'ol/geom.js'
import { Vector as VectorLayer } from 'ol/layer.js'
import { Stroke } from 'ol/style.js'
import type MapBrowserEvent from 'ol/MapBrowserEvent'
import type OlMap from 'ol/Map'

// vue date range
import { useDatasets } from '@/composables/useDatasets'
import type { Dataset } from '@/composables/useDatasets'
import type { Coordinate } from 'ol/coordinate'

import { useSites } from '@/composables/useSites'

// info drawer
import { useDrawer } from '@/composables/useDrawer'

const MapNotesLayer = RawMapNotesLayer as any

export default defineComponent({
    components: {
        MapComponent,
        MapNotesLayer,
        FileListVue
    },

    setup() {
        const drawer = useDrawer()

        const route = useRoute()
        const router = useRouter()
        const siteId = computed(() => route.params.site_id) as ComputedRef<string>

        const goBackToSite = () => {
            if (siteId.value) {
                router.push(`/site/${siteId.value}/`)
            }
        }
        console.log('Site ID:', siteId.value)
        console.log('route params:', route.params)
        const mapStore = useMapStore()
        const datasetsAdapter = useDatasets(route.params.site_id as string)
        const sitesAdapter = useSites()
        const selectedDataset: Ref<Dataset | null> = ref(null)
        const enableMultipleLayers = ref(false)
        const selectedDatasetIds = ref<number[]>([])
        const slider = document.getElementById('coherence-threshold')
        const cohThreshold = ref(0.7)
        const velThreshold = ref(0.005)
        const baseWidth = ref(720)
        const pointSize = ref(2)
        const maxConcurrentRequests = ref(10)
        const selectedPoint = ref(null)
        const siteObject = ref(null)
        const isChartOverlayOpen = ref(false)
        const aoiLayerVisible = ref(true)
        const showLegendOverlay = ref(false)
        let aoiLayer: VectorLayer<VectorSource> | null = null
        let popupLeaderLayer: VectorLayer<VectorSource> | null = null
        let popupLeaderFeature: Feature<LineString> | null = null
        let popupAnchorCoordinate: Coordinate | null = null
        let removePopupDragListeners: (() => void) | null = null

        const hasSiteGeometry = (site: unknown): site is { geometry: string } => {
            return (
                !!site &&
                typeof site === 'object' &&
                'geometry' in site &&
                typeof (site as { geometry?: unknown }).geometry === 'string'
            )
        }

        const categories = ref([
            { name: 'Structural Health Monitoring', key: 'S', route_key: 'shm', active: true },
            { name: 'Coastal Monitoring', key: 'C', route_key: 'coastal', active: false },
            {
                name: 'Environmental Monitoring',
                key: 'E',
                route_key: 'environmental',
                active: false
            },
            { name: 'Alerts and change detection', key: 'A', route_key: 'alerts', active: false }
        ])
        const selectedCategory = ref('S')
        
        // Track if controls have changed to show reload button
        const controlsChanged = ref(false)
        const initialControlValues = ref({
            cohThreshold: cohThreshold.value,
            velThreshold: velThreshold.value,
            pointSize: pointSize.value,
            maxConcurrentRequests: maxConcurrentRequests.value,
            selectedCategory: selectedCategory.value
        })
        
        const reloadWithNewControls = () => {
            // Reload the current dataset with new control values
            if (selectedDataset.value || selectedDatasetIds.value.length > 0) {
                // Clear and reload the layer with new settings
                findFeaturesForDataset()
            }
            
            // Reset the controls changed flag and update initial values
            controlsChanged.value = false
            initialControlValues.value = {
                cohThreshold: cohThreshold.value,
                velThreshold: velThreshold.value,
                pointSize: pointSize.value,
                maxConcurrentRequests: maxConcurrentRequests.value,
                selectedCategory: selectedCategory.value
            }
        }

        const updatePopupWidth = (mapWidth: number) => {
            // Set the width of the popup as a proportion of the map width
            console.log('map width is', mapWidth)
            baseWidth.value = mapWidth * 0.6666
            console.log(
                'baseWidth.value (setting initial popup width for chart) set to:',
                baseWidth.value
            )
        }

        // Function to copy time series data to clipboard in column format
        const copyTimeSeriesData = (
            dates: string[],
            values: number[],
            lat: number,
            lon: number
        ) => {
            // Create header column
            const headers = ['lat', 'lon', ...dates]

            // Create values column
            const valuesColumn = [
                lat.toString(),
                lon.toString(),
                ...values.map((v) => v.toString())
            ]

            // Join each column with newlines
            const headerText = headers.join('\n')
            const valueText = valuesColumn.join('\n')

            // Create tab-separated text for easy pasting into Excel
            const clipboardText = headers
                .map((header, index) => `${header}\t${valuesColumn[index]}`)
                .join('\n')

            // Copy to clipboard
            navigator.clipboard
                .writeText(clipboardText)
                .then(() => {
                    console.log('Time series data copied to clipboard')
                    // You could add a toast notification here
                })
                .catch((err) => {
                    console.error('Failed to copy to clipboard:', err)
                })
        }

        const featuresContainer = ref(new Collection())
        const globalGeoJsonFormat = new GeoJSON()

        console.log(siteId.value)
        const handleDatasetChange = (dataset: Dataset) => {
            // search for the dataset in the datasets array
            // const dataset = datasetsAdapter.datasets.value.find(
            //   (dataset) => dataset.name === datasetName
            // ) as Dataset
            selectedDataset.value = dataset
            if (dataset.id != null) {
                selectedDatasetIds.value = [dataset.id]
            }
            console.log(
                `Selected dataset: ${selectedDataset.value?.id} - ${selectedDataset.value?.name}`
            )
            // zoomToDataSet();
        }

        const set_preset = () => {
            if (selectedCategory.value === 'S') {
                cohThreshold.value = 0.8
                velThreshold.value = 0.005
            } else if (selectedCategory.value === 'C') {
                cohThreshold.value = 0.6
                velThreshold.value = 0.01
            } else if (selectedCategory.value === 'E') {
                cohThreshold.value = 0.4
                velThreshold.value = 0.01
            } else if (selectedCategory.value === 'A') {
                cohThreshold.value = 0.7
                velThreshold.value = 0.005
            }
            // set the active category
            categories.value.forEach((cat) => {
                cat.active = cat.key === selectedCategory.value
            })
        }

        const getCoherenceAssessment = (coherence: number) => {
            if (coherence >= 0.8) {
                return 'This is a reliable measurement point with relatively low noise.'
            }
            if (coherence >= 0.6) {
                return 'This measurement point shows some noise, so care should be taken when interpreting the time-series history.'
            }
            if (coherence >= 0.4) {
                return 'This measurement point has elevated noise, so any apparent displacement history should be treated with caution and cross-checked against nearby points and independent data sources.'
            }
            return 'This measurement point has low coherence and limited reliability, so it should not be used in isolation for interpretation.'
        }

        const getCoherenceAssessmentClass = (coherence: number) => {
            if (coherence >= 0.8) {
                return 'coherence-assessment is-reliable'
            }
            if (coherence >= 0.6) {
                return 'coherence-assessment is-caution'
            }
            return 'coherence-assessment is-noisy'
        }

        const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        const getDatasetDisplayName = (dataset: Dataset) => {
            const rawLabel = String(dataset.displayName || dataset.name || '').trim()
            const rawSite = String(siteId.value || '').trim()
            if (!rawSite) {
                return rawLabel
            }

            const candidateSiteNames = Array.from(
                new Set([rawSite, rawSite.replace(/[-_]+/g, ' ')])
            ).filter(Boolean)

            let cleaned = rawLabel
            candidateSiteNames.forEach((siteName) => {
                cleaned = cleaned.replace(new RegExp(`^${escapeRegExp(siteName)}\\s*`, 'i'), '')
            })

            return cleaned.replace(/^[-_:,\s]+/, '').trim()
        }

        const getDatasetBatchIndex = (dataset: Dataset) => {
            const label = getDatasetDisplayName(dataset)
            const match = label.match(/\bbatch[\s_-]*(\d+)\b/i)
            if (!match) {
                return null
            }
            return Number(match[1])
        }

        const sortDatasets = (datasets: Dataset[]) => {
            return [...datasets].sort((a, b) => {
                const aBatch = getDatasetBatchIndex(a)
                const bBatch = getDatasetBatchIndex(b)

                if (aBatch !== null && bBatch !== null && aBatch !== bBatch) {
                    return aBatch - bBatch
                }
                if (aBatch !== null && bBatch === null) {
                    return -1
                }
                if (aBatch === null && bBatch !== null) {
                    return 1
                }

                return getDatasetDisplayName(a).localeCompare(getDatasetDisplayName(b), undefined, {
                    numeric: true,
                    sensitivity: 'base'
                })
            })
        }

        const isVerticalDataset = (dataset: Dataset) => {
            const label = getDatasetDisplayName(dataset)
            return /\bvertical\b/i.test(label)
        }

        const isEastWestDataset = (dataset: Dataset) => {
            const label = getDatasetDisplayName(dataset)
            return /\beast[\s_-]*west\b/i.test(label)
        }

        const isLineOfSightDataset = (dataset: Dataset) => {
            const label = getDatasetDisplayName(dataset)
            return /\blos\b|\bline[\s_-]*of[\s_-]*sight\b/i.test(label)
        }

        const isHiddenTempDataset = (dataset: Dataset) => {
            const label = getDatasetDisplayName(dataset)
            return /temp\s*$/i.test(label)
        }

        const getDatasetSectionKey = (dataset: Dataset) => {
            if (isVerticalDataset(dataset)) {
                return 'vertical'
            }
            if (isEastWestDataset(dataset)) {
                return 'eastWest'
            }
            if (isLineOfSightDataset(dataset)) {
                return 'los'
            }
            return 'other'
        }

        const sortedDatasets = computed(() =>
            sortDatasets(datasetsAdapter.datasets.value).filter(
                (dataset) => !isHiddenTempDataset(dataset)
            )
        )

        const datasetSections = computed(() => {
            const groups = {
                los: [] as Dataset[],
                vertical: [] as Dataset[],
                eastWest: [] as Dataset[],
                other: [] as Dataset[]
            }

            sortedDatasets.value.forEach((dataset) => {
                groups[getDatasetSectionKey(dataset)].push(dataset)
            })

            return [
                {
                    key: 'los',
                    title: 'LoS datasets',
                    datasets: groups.los
                },
                {
                    key: 'vertical',
                    title: 'Vertical datasets',
                    datasets: groups.vertical
                },
                {
                    key: 'eastWest',
                    title: 'East-West datasets',
                    datasets: groups.eastWest
                },
                {
                    key: 'other',
                    title: 'Other PSI datasets',
                    datasets: groups.other
                }
            ].filter((section) => section.datasets.length > 0)
        })

        // check route params for a 'psi_settings' key
        if (route.params.psi_settings) {
            console.log('Route params:', route.params.psi_settings)
            const psi_settings = route.params.psi_settings
            console.log('Parsed psi_settings:', psi_settings)
            // psi_settings might be 'shm', 'coastal', 'environmental', 'alerts'
            if (psi_settings) {
                const cat = categories.value.find((cat) => cat.route_key === psi_settings)
                if (cat) {
                    selectedCategory.value = cat.key
                    console.log('Selected category:', selectedCategory.value)
                    set_preset()
                }
            }
        }
        console.log('123')

        function hsvToRgb(h: number, s: number, v: number) {
            // Convert hue to a scale between 0 and 240 degrees (hue in degrees)
            h = h * 240 // Scale the input hue [0, 1] to [0, 240]

            let r = 0,
                g = 0,
                b = 0
            const i = Math.floor(h / 60) // Find the sector of the hue
            const f = h / 60 - i // Fractional part of the hue
            const p = v * (1 - s) // p = value * (1 - saturation)
            const q = v * (1 - f * s) // q = value * (1 - f * saturation)
            const t = v * (1 - (1 - f) * s) // t = value * (1 - (1 - f) * saturation)

            switch (i) {
                case 0:
                    ;(r = v), (g = t), (b = p)
                    break
                case 1:
                    ;(r = q), (g = v), (b = p)
                    break
                case 2:
                    ;(r = p), (g = v), (b = t)
                    break
                case 3:
                    ;(r = p), (g = q), (b = v)
                    break
                case 4:
                    ;(r = t), (g = p), (b = v)
                    break
                case 5:
                    ;(r = v), (g = p), (b = q)
                    break
            }

            // Convert RGB to [0, 255] range
            return [Math.round(b * 255), Math.round(g * 255), Math.round(r * 255)]
        }

        function linspace(start: number, end: number, numStops: number) {
            const step = (end - start) / (numStops - 1)
            const result = []

            for (let i = 0; i < numStops; i++) {
                result.push(start + i * step)
            }

            return result
        }
        const getVelocityRampStops = (threshold: number) => {
            const thresholds = linspace(-threshold, threshold, 21)
            const colors = thresholds.map((value, index) => {
                // Generate a hue value that increases with each threshold
                const hue = index / (thresholds.length - 1) // from 0 to 1
                // const saturation = 1;  // full saturation
                // reduce saturation to 0.7 at half way
                const saturation = 0.5 + 0.5 * Math.abs(hue - 0.5) * 2

                const brightness = 1 // full brightness
                const [r, g, b] = hsvToRgb(hue, saturation, brightness) // Convert HSV to RGB
                return `rgb(${r}, ${g}, ${b})`
            })

            return { thresholds, colors }
        }

        const legendGradientStyle = computed(() => {
            const { colors } = getVelocityRampStops(velThreshold.value)
            return {
                background: `linear-gradient(90deg, ${[...colors].reverse().join(', ')})`
            }
        })

        const toggleLegendOverlay = () => {
            showLegendOverlay.value = !showLegendOverlay.value
        }

        function generateColorMap(threshold: number) {
            const { thresholds, colors } = getVelocityRampStops(threshold)

            // Generate the color map using the thresholds and generated colors
            return [
                'interpolate',
                ['linear'],
                ['get', 'velocity'],
                ...thresholds.flatMap((value, index) => [value, colors[index]])
            ]
        }

        type ScattererPoint = [number | null, number | null, number | null, number | null, number | null]
        type ScattererPayload = {
            id: string
            lat: number
            lon: number
            coherence: number
            velocity: number
            heightError: number
        }

        function addPointsToMap(
            batchData: ScattererPayload[],
            targetSource: { addFeatures: (features: Feature[]) => void }
        ) {
            const features = batchData.map((point) => {
                // Create a feature for each point
                const feature = new Feature({
                    geometry: new Point(fromLonLat([point.lon, point.lat])), // Convert coordinates to map projection
                    coherence: point.coherence,
                    velocity: point.velocity,
                    height_error: point.heightError,
                    scatterer_id: point.id,
                    source_lat: point.lat,
                    source_lon: point.lon
                })
                return feature
            })

            // Add features to the source
            targetSource.addFeatures(features)
        }
        const zoomToDataSet = () => {
            if (!mapStore.map) {
                console.log('No map')
                return
            }
            // zoom to the extent of the dataset
            if (!selectedDataset.value) {
                console.log('No dataset selected')
                return
            }
            if (!selectedDataset.value.extent) {
                console.log('No extent found for dataset')
                return
            }
            // type check that extent is a string
            if (typeof selectedDataset.value.extent !== 'string') {
                console.log('Extent is not a string')
                return
            }
            var extent: string = selectedDataset.value.extent
            if (!extent) {
                console.log('No extent found for dataset')
                return
            }
            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }
            const wkt = new WKT()
            console.log('Original extent:', extent)
            // openlayers doesn't like the SRID=4326; prefix
            extent = extent.replace(/SRID=4326;\s*/i, '')
            console.log('Modified extent:', extent)
            const extentGeometry = wkt.readGeometry(extent)
            // const polygon = wkt.readGeometry("SRID=4326;POLYGON ((0 1, 0 2, 1 2, 1 1, 0 1))");
            console.log(extentGeometry)
            if (!(extentGeometry instanceof Polygon)) {
                console.log('Extent geometry is not a polygon')
                return
            }
            const transformedExtent = extentGeometry.transform(
                'EPSG:4326',
                mapStore.map.getView().getProjection()
            )

            // OL expects polygon to be:
            // [[WN], [WS], [ES], [EN], [WN]]
            console.log('aloha')
            const olSiteCoords = extentGeometry
                .getCoordinates()[0]
                .map((coord: Coordinate) => fromLonLat([coord[0], coord[1]]))
            const sitePolygon = new Polygon([olSiteCoords])
            const siteFeature = new Feature(sitePolygon)
            // Create a vector source and layer
            const vectorSource = new VectorSource({
                features: [siteFeature]
            })
            const vectorLayer = new VectorLayer({
                source: vectorSource
                // style: polyStyle
            })
            // Add the vector layer to the map
            mapStore.map.addLayer(vectorLayer)
            // mapStore.map.getView().fit(transformedExtent.getExtent(), { duration: 2000 })
            mapStore.map.getView().fit(sitePolygon, { padding: [100, 100, 100, 100] })
        }

        const zoomToExtent = (extent: string) => {
            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }
            const wkt = new WKT()

            // openlayers doesn't like the SRID=4326; prefix
            extent = extent.replace(/SRID=4326;\s*/i, '')
            console.log('Modified extent:', extent)
            const extentGeometry = wkt.readGeometry(extent) as Polygon
            const transformedExtent = extentGeometry.transform(
                'EPSG:4326',
                mapStore.map.getView().getProjection()
            )

            const polyStyle = new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.0)'
                }),
                stroke: new Stroke({
                    color: '#dddddd',
                    width: 3,
                    lineDash: [3, 1],
                    lineCap: 'butt'
                })
            })
            const siteFeature = new Feature(transformedExtent)
            // Create a vector source and layer
            const vectorSource = new VectorSource({
                features: [siteFeature]
            })
            const vectorLayer = new VectorLayer({
                source: vectorSource,
                style: polyStyle
            })
            // Add the vector layer to the map
            mapStore.map.addLayer(vectorLayer)
            
            // Store reference to AOI layer for toggle functionality
            aoiLayer = vectorLayer
            
            mapStore.map.getView().fit(transformedExtent, { padding: [100, 100, 100, 100] })
        }

        const toggleAOI = () => {
            if (!aoiLayer) {
                console.log('No AOI layer found')
                return
            }
            
            aoiLayerVisible.value = !aoiLayerVisible.value
            aoiLayer.setVisible(aoiLayerVisible.value)
            console.log('AOI visibility toggled to:', aoiLayerVisible.value)
        }

        const isLoading = ref(false)
        const totalFeatures = ref(0)
        const loadedFeatures = ref(0)
        const activeLoadCount = ref(0)
        const isAnyLayerLoading = computed(() => activeLoadCount.value > 0)

        type LayerEntry = {
            datasetId: number
            layer: WebGLPointsLayer<VectorSource>
            source: VectorSource
            loaded: boolean
            loading: boolean
            status: 'idle' | 'loading' | 'loaded' | 'skipped' | 'error'
            settingsKey: string
            abortController: AbortController | null
        }

        const layerRegistry = ref<Map<number, LayerEntry>>(new Map())
        const loadingProgress = computed(() => {
            if (totalFeatures.value === 0) return 0
            const result = Math.floor((loadedFeatures.value / totalFeatures.value) * 100)
            console.log(`Progress: ${loadedFeatures.value}/${totalFeatures.value} = ${result}%`)
            return result
        })

        const getSettingsKey = () => {
            return JSON.stringify({
                cohThreshold: cohThreshold.value,
                velThreshold: velThreshold.value,
                pointSize: pointSize.value,
                maxConcurrentRequests: maxConcurrentRequests.value,
                selectedCategory: selectedCategory.value
            })
        }

        const createPointsLayer = () => {
            const source = new VectorSource()
            const layer = new WebGLPointsLayer({
                source,
                style: {
                    'circle-radius': pointSize.value,
                    'circle-fill-color': generateColorMap(velThreshold.value),
                    'circle-rotate-with-view': false,
                    'circle-displacement': [0, 0]
                }
            })
            return { layer, source }
        }

        const ensureLayerForDataset = (datasetId: number) => {
            const existing = layerRegistry.value.get(datasetId)
            if (existing) {
                return existing
            }

            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }

            const { layer, source } = createPointsLayer()
            mapStore.map.addLayer(layer)

            const entry: LayerEntry = {
                datasetId,
                layer,
                source,
                loaded: false,
                loading: false,
                status: 'idle',
                settingsKey: '',
                abortController: null
            }

            layerRegistry.value.set(datasetId, entry)
            return entry
        }

        const rebuildLayerForEntry = (entry: LayerEntry) => {
            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }

            const previousVisible = (entry.layer as any).getVisible
                ? (entry.layer as any).getVisible()
                : true

            mapStore.map.removeLayer(entry.layer as any)

            const { layer, source } = createPointsLayer()
            if ((layer as any).setVisible) {
                ;(layer as any).setVisible(previousVisible)
            }
            mapStore.map.addLayer(layer)

            entry.layer = layer
            entry.source = source
            entry.loaded = false
            entry.status = 'idle'
        }

        const removeLayerForDataset = (datasetId: number) => {
            const entry = layerRegistry.value.get(datasetId)
            if (!entry) {
                return
            }

            entry.abortController?.abort()
            if (mapStore.map) {
                mapStore.map.removeLayer(entry.layer as any)
            }
            layerRegistry.value.delete(datasetId)
        }

        const removeAllLayersExcept = (keepDatasetId: number) => {
            const ids: number[] = Array.from(layerRegistry.value.keys())
            ids.forEach((datasetId) => {
                if (datasetId !== keepDatasetId) {
                    removeLayerForDataset(datasetId)
                }
            })
        }

        const getDatasetIdsToLoad = () => {
            if (enableMultipleLayers.value) {
                return selectedDatasetIds.value.filter((id) => Number.isFinite(id))
            }
            if (selectedDataset.value?.id == null) {
                return []
            }
            return [selectedDataset.value.id]
        }

        const getDatasetLayerStatus = (datasetId?: number | null) => {
            if (datasetId == null) {
                return 'idle'
            }
            const entry = layerRegistry.value.get(datasetId)
            return entry?.status ?? 'idle'
        }

        const getDatasetLayerStatusLabel = (datasetId?: number | null) => {
            const status = getDatasetLayerStatus(datasetId)
            if (status === 'idle') {
                return 'Not loaded'
            }
            if (status === 'skipped') {
                return 'Present'
            }
            if (status === 'loading') {
                return 'Loading'
            }
            if (status === 'loaded') {
                return 'Loaded'
            }
            if (status === 'error') {
                return 'Error'
            }
            return status
        }

        const getDatasetLayerStatusClass = (datasetId?: number | null) => {
            const status = getDatasetLayerStatus(datasetId)
            if (status === 'skipped') {
                return 'status-present'
            }
            return `status-${status}`
        }

        const debugClickConsistency = ref(true)

        const getDatasetIdForLayer = (layer: unknown): number | null => {
            for (const [datasetId, entry] of layerRegistry.value.entries()) {
                if ((entry.layer as unknown) === layer) {
                    return datasetId
                }
            }
            return null
        }

        const getClickedFeatureContext = (map: OlMap, event: MapBrowserEvent<UIEvent>) => {
            let clickedFeature: any = null
            let clickedDatasetId: number | null = null

            map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                clickedFeature = feature as Feature
                clickedDatasetId = getDatasetIdForLayer(layer)
                return true
            })

            return { clickedFeature, clickedDatasetId }
        }

        const getFeatureNumberProperty = (feature: any, key: string) => {
            const value = feature?.get?.(key)
            return Number(value)
        }

        const getFeatureProperty = (feature: any, key: string) => {
            return feature?.get?.(key)
        }

        const getScattererAnchorCoordinate = (locationWkt: unknown): Coordinate | null => {
            if (typeof locationWkt !== 'string' || !locationWkt) {
                return null
            }

            const wkt = new WKT()
            const normalized = locationWkt.replace(/SRID=4326;\s*/i, '')
            const geometry = wkt.readGeometry(normalized)
            if (!(geometry instanceof Point)) {
                return null
            }

            const rawCoords = geometry.getCoordinates()
            if (!Array.isArray(rawCoords) || rawCoords.length < 2) {
                return null
            }

            const x = Number(rawCoords[0])
            const y = Number(rawCoords[1])
            if (!Number.isFinite(x) || !Number.isFinite(y)) {
                return null
            }

            // Backend currently returns WKT as POINT(lat lon) for this flow.
            // Convert explicitly to map coords using lon=y, lat=x.
            const resolved = fromLonLat([y, x])
            return [resolved[0], resolved[1]]
        }

        const unloadSelectedLayers = () => {
            const ids = enableMultipleLayers.value
                ? [...selectedDatasetIds.value]
                : selectedDataset.value?.id != null
                  ? [selectedDataset.value.id]
                  : []

            ids.forEach((datasetId) => removeLayerForDataset(datasetId))
            selectedDatasetIds.value = selectedDatasetIds.value.filter((id) => !ids.includes(id))

            if (selectedDataset.value?.id != null && ids.includes(selectedDataset.value.id)) {
                const replacement = datasetsAdapter.datasets.value.find((d) => selectedDatasetIds.value.includes(Number(d.id)))
                selectedDataset.value = replacement ?? null
            }
        }

        const toggleDatasetSelection = (dataset: Dataset, checked: boolean) => {
            if (dataset.id == null) {
                return
            }

            if (checked) {
                if (!selectedDatasetIds.value.includes(dataset.id)) {
                    selectedDatasetIds.value.push(dataset.id)
                }
                selectedDataset.value = dataset
                return
            }

            selectedDatasetIds.value = selectedDatasetIds.value.filter((id) => id !== dataset.id)
            if (selectedDataset.value?.id === dataset.id) {
                const replacement = datasetsAdapter.datasets.value.find((d) => d.id === selectedDatasetIds.value[0])
                selectedDataset.value = replacement ?? null
            }
            removeLayerForDataset(dataset.id)
        }

        watch(enableMultipleLayers, (enabled) => {
            if (enabled) {
                const selectedId = selectedDataset.value?.id
                if (selectedId != null && !selectedDatasetIds.value.includes(selectedId)) {
                    selectedDatasetIds.value.push(selectedId)
                }
                return
            }

            if (!enabled) {
                const keepId = selectedDataset.value?.id
                if (keepId != null) {
                    selectedDatasetIds.value = [keepId]
                    removeAllLayersExcept(keepId)
                } else {
                    selectedDatasetIds.value = []
                }
            }
        })

        const findFeaturesForDataset = async () => {
            const datasetIds = getDatasetIdsToLoad()
            if (datasetIds.length === 0) {
                console.log('No dataset selected')
                return
            }

            if (!enableMultipleLayers.value) {
                removeAllLayersExcept(datasetIds[0])
            }

            const settingsKey = getSettingsKey()
            isLoading.value = true
            loadedFeatures.value = 0
            totalFeatures.value = 0

            const loadOneDataset = async (datasetId: number) => {
                const layerEntry = ensureLayerForDataset(datasetId)

                if (layerEntry.loading) {
                    return
                }

                if (layerEntry.loaded && layerEntry.settingsKey === settingsKey) {
                    console.log(`Skipping dataset ${datasetId}: already loaded with same settings`)
                    layerEntry.status = 'skipped'
                    return
                }

                // Style-related controls (point size / color ramp) require rebuilding the
                // WebGL layer for already-loaded datasets so updates actually render.
                if (layerEntry.settingsKey && layerEntry.settingsKey !== settingsKey) {
                    rebuildLayerForEntry(layerEntry as LayerEntry)
                }

                layerEntry.abortController?.abort()
                const abortController = new AbortController()
                layerEntry.abortController = abortController
                layerEntry.loading = true
                layerEntry.status = 'loading'
                layerEntry.settingsKey = settingsKey
                layerEntry.source.clear()

                activeLoadCount.value += 1
                try {
                    const countResponse = await fetch(`/api/scatterer-info/?limit=1&dataset=${datasetId}`, {
                        signal: abortController.signal
                    })
                    if (!countResponse.ok) {
                        throw new Error(`HTTP error! status: ${countResponse.status}`)
                    }

                    const countData = await countResponse.json()
                    const featureCount = Number(countData.count) || 0
                    totalFeatures.value += featureCount

                    if (featureCount === 0) {
                        layerEntry.loaded = true
                        layerEntry.status = 'loaded'
                        return
                    }

                    const chunkOffsets: number[] = []
                    for (let offset = 0; offset < featureCount; offset += 10000) {
                        chunkOffsets.push(offset)
                    }

                    const concurrencyLimit = Math.max(1, maxConcurrentRequests.value || 1)
                    let nextChunkIndex = 0

                    const loadChunk = async (offset: number) => {
                        const batchSize = Math.min(10000, featureCount - offset)
                        const response = await fetch(
                            `/api/scatterer-info/?limit=${batchSize}&offset=${offset}&dataset=${datasetId}`,
                            { signal: abortController.signal }
                        )

                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`)
                        }

                        const batchData = await response.json()
                        const data = batchData.data as Record<string, ScattererPoint>
                        const filteredByCoh = Object.entries(data)
                            .filter(([, point]) => {
                                return (
                                    point[0] !== null &&
                                    point[1] !== null &&
                                    point[2] !== null &&
                                    point[3] !== null &&
                                    point[4] !== null &&
                                    Number(point[2]) > cohThreshold.value
                                )
                            })
                            .map(([id, point]) => ({
                                id,
                                lat: Number(point[0]),
                                lon: Number(point[1]),
                                coherence: Number(point[2]),
                                velocity: Number(point[3]),
                                heightError: Number(point[4])
                            }))

                        addPointsToMap(filteredByCoh, layerEntry.source)
                        loadedFeatures.value += batchSize
                    }

                    const workers = Array.from(
                        { length: Math.min(concurrencyLimit, chunkOffsets.length) },
                        async () => {
                            while (nextChunkIndex < chunkOffsets.length) {
                                const currentIndex = nextChunkIndex++
                                await loadChunk(chunkOffsets[currentIndex])
                            }
                        }
                    )

                    await Promise.all(workers)
                    layerEntry.loaded = true
                    layerEntry.status = 'loaded'
                } catch (error) {
                    if ((error as Error).name === 'AbortError') {
                        console.log(`Load aborted for dataset ${datasetId}`)
                        layerEntry.status = 'idle'
                    } else {
                        console.error(`Error loading dataset ${datasetId}:`, error)
                        layerEntry.status = 'error'
                    }
                } finally {
                    layerEntry.loading = false
                    layerEntry.abortController = null
                    activeLoadCount.value = Math.max(0, activeLoadCount.value - 1)
                }
            }

            try {
                await Promise.all(datasetIds.map((datasetId) => loadOneDataset(datasetId)))
            } finally {
                isLoading.value = false
            }
        }

        onMounted(() => {
            const map = mapStore.map
            if (!map) {
                throw new Error('Map not initialized')
            }

            datasetsAdapter.fetchDatasets().then((response) => {
                console.log(datasetsAdapter.datasets.value)
                // zoom to first dataset extent
                if (datasetsAdapter.datasets.value.length > 0) {
                    const firstExtent = datasetsAdapter.datasets.value[0].extent
                    if (typeof firstExtent === 'string' && firstExtent.length > 0) {
                        zoomToExtent(firstExtent)
                    }
                } else {
                    // zoom to the site extent
                    sitesAdapter.fetchItems().then(() => {
                        const site = sitesAdapter.sites.value.find(
                            (site) => site.name === siteId.value
                        )
                        console.log('Site:', site)
                        if (!site || !hasSiteGeometry(site)) {
                            console.log('No site geometry available to zoom extent')
                            return
                        }
                        // convert the [N, E, S, W] string to a WKT polygon
                        const noBrackets = site.geometry.slice(1, site.geometry.length - 1)
                        const limits = noBrackets
                            .split(',')
                            .map((coord: string) => parseFloat(coord))
                        const wktpolygon = `POLYGON((${limits[1]} ${limits[0]}, ${limits[3]} ${limits[0]}, ${limits[3]} ${limits[2]}, ${limits[1]} ${limits[2]}, ${limits[1]} ${limits[0]}))`
                        zoomToExtent(wktpolygon)
                    })
                }
            })

            const pop_container = document.getElementById('popup')
            const pop_content = document.getElementById('popup-content')
            const pop_closer = document.getElementById('popup-closer')

            // Make sure these are not null
            if (pop_container === null || pop_content === null || pop_closer === null) {
                throw new Error('popup elements are null')
            }

            const pop_overlay = new Overlay({
                element: pop_container
            })
            pop_overlay.setPosition(undefined)

            const popupLeaderSource = new VectorSource()
            popupLeaderFeature = new Feature(new LineString([]))
            popupLeaderSource.addFeature(popupLeaderFeature)
            popupLeaderLayer = new VectorLayer({
                source: popupLeaderSource,
                style: new Style({
                    stroke: new Stroke({
                        color: 'rgba(255, 255, 255, 0.65)',
                        width: 2.5,
                        lineDash: [3, 1]
                    })
                })
            })
            map.addLayer(popupLeaderLayer)

            const updatePopupLeaderLine = () => {
                if (!popupLeaderFeature) {
                    return
                }
                const popupPosition = pop_overlay.getPosition() as Coordinate | undefined
                if (!popupPosition || !popupAnchorCoordinate) {
                    popupLeaderFeature.setGeometry(new LineString([]))
                    return
                }

                const targetElement = map.getTargetElement()
                if (!targetElement) {
                    popupLeaderFeature.setGeometry(new LineString([]))
                    return
                }

                const popupRect = pop_container.getBoundingClientRect()
                const mapRect = targetElement.getBoundingClientRect()
                const popupCenterPixel: [number, number] = [
                    popupRect.left - mapRect.left + popupRect.width / 2,
                    popupRect.top - mapRect.top + popupRect.height / 2
                ]
                const popupCenterCoordinate = map.getCoordinateFromPixel(popupCenterPixel)
                if (!popupCenterCoordinate) {
                    popupLeaderFeature.setGeometry(new LineString([]))
                    return
                }

                popupLeaderFeature.setGeometry(new LineString([popupAnchorCoordinate, popupCenterCoordinate]))
            }

            const setupPopupDragging = () => {
                let isDragging = false
                let dragStartMouse: [number, number] | null = null
                let dragStartOverlayCoord: Coordinate | null = null

                const isInteractiveElement = (target: EventTarget | null) => {
                    if (!(target instanceof HTMLElement)) {
                        return false
                    }
                    return Boolean(
                        target.closest(
                            '#popup-closer, #copy-timeseries-btn, canvas, button, a, input, select, textarea'
                        )
                    )
                }

                const onMouseDown = (event: MouseEvent) => {
                    if (event.button !== 0) {
                        return
                    }
                    if (isInteractiveElement(event.target)) {
                        return
                    }

                    const currentOverlayPosition = pop_overlay.getPosition() as Coordinate | undefined
                    if (!currentOverlayPosition) {
                        return
                    }

                    isDragging = true
                    dragStartMouse = [event.clientX, event.clientY]
                    dragStartOverlayCoord = [currentOverlayPosition[0], currentOverlayPosition[1]]
                    pop_container.style.cursor = 'grabbing'
                    event.preventDefault()
                }

                const onMouseMove = (event: MouseEvent) => {
                    if (!isDragging || !dragStartMouse || !dragStartOverlayCoord) {
                        return
                    }

                    const startPixel = map.getPixelFromCoordinate(dragStartOverlayCoord)
                    const nextPixel: [number, number] = [
                        startPixel[0] + (event.clientX - dragStartMouse[0]),
                        startPixel[1] + (event.clientY - dragStartMouse[1])
                    ]
                    const nextCoordinate = map.getCoordinateFromPixel(nextPixel)
                    if (!nextCoordinate) {
                        return
                    }

                    pop_overlay.setPosition(nextCoordinate)
                    updatePopupLeaderLine()
                }

                const onMouseUp = () => {
                    if (!isDragging) {
                        return
                    }
                    isDragging = false
                    dragStartMouse = null
                    dragStartOverlayCoord = null
                    pop_container.style.cursor = ''
                }

                pop_container.addEventListener('mousedown', onMouseDown)
                window.addEventListener('mousemove', onMouseMove)
                window.addEventListener('mouseup', onMouseUp)

                removePopupDragListeners = () => {
                    pop_container.removeEventListener('mousedown', onMouseDown)
                    window.removeEventListener('mousemove', onMouseMove)
                    window.removeEventListener('mouseup', onMouseUp)
                }
            }

            setupPopupDragging()

            function close_chart_popup() {
                if (pop_container) {
                    pop_container.style.display = 'none'
                }
                pop_overlay.setPosition(undefined)
                popupAnchorCoordinate = null
                updatePopupLeaderLine()
                isChartOverlayOpen.value = false
            }

            pop_closer.onclick = function () {
                close_chart_popup()
            }

            const resolvePopupDataset = (event: any) => {
                let clickedDatasetId: number | null = null
                map.forEachFeatureAtPixel(event.pixel, (_feature, layer) => {
                    if (!layer || clickedDatasetId != null) {
                        return false
                    }

                    for (const [datasetId, entry] of layerRegistry.value.entries()) {
                        if ((entry.layer as any) === (layer as any)) {
                            clickedDatasetId = datasetId
                            return true
                        }
                    }
                    return false
                })

                if (clickedDatasetId != null) {
                    const byLayer = datasetsAdapter.datasets.value.find((d) => Number(d.id) === clickedDatasetId)
                    if (byLayer) {
                        return byLayer
                    }
                }

                const loadIds = getDatasetIdsToLoad()
                const fallbackId = selectedDataset.value?.id ?? loadIds[0]
                if (fallbackId == null) {
                    return null
                }

                return datasetsAdapter.datasets.value.find((d) => Number(d.id) === fallbackId) ?? null
            }

            // Add an onclick-event to the map
            map.on('click', async (event) => {
                const popupDataset = resolvePopupDataset(event)
                if (!popupDataset || popupDataset.id == null) {
                    console.log('No dataset selected')
                    return
                }

                const { clickedFeature, clickedDatasetId } = getClickedFeatureContext(map, event)

                // Check if the chart overlay is open
                if (isChartOverlayOpen.value) {
                    console.log('Chart overlay is open')
                    // close it
                    close_chart_popup()
                    return
                }

                // Get the coordinates of the pointer event
                const coords = event.coordinate
                popupAnchorCoordinate = [coords[0], coords[1]]
                // Y axis label
                // console.log('Setting popup position to: ', coords)
                pop_overlay.setPosition(coords)
                updatePopupLeaderLine()
                // // deep copy the coords
                // const popup_coords = coords.slice()
                // // get half the height and width
                // const halfHeight = pop_container.clientHeight / 2
                // const halfWidth = pop_container.clientWidth / 2
                // // convert map pixels to coordinates
                // const centreCoords = map.getCoordinateFromPixel([halfWidth, halfHeight])
                // const originCoords = map.getCoordinateFromPixel([0, 0])
                // const halfWidthInCoords = centreCoords[0] - originCoords[0]
                // const halfHeightInCoords = centreCoords[1] - originCoords[1]
                // console.log('Half width in coords: ', halfWidthInCoords)
                // console.log('Half height in coords: ', halfHeightInCoords)
                // pop_overlay.setPosition([popup_coords[0] - halfWidthInCoords, popup_coords[1] - halfHeightInCoords])
                // console.log('Setting popup position to: ', [popup_coords[0] - halfWidthInCoords, popup_coords[1] - halfHeightInCoords])
                // console.log('Popup overlay position set to: ', pop_overlay.getPosition())

                // // set a watch for 5seconds
                // setTimeout(() => {
                //   const popPosAfter5 = pop_overlay.getPosition()
                //   if (popPosAfter5) {
                //     console.log('Popup overlay is still open after 5 seconds:', popPosAfter5)
                //     pop_overlay.setPosition(popPosAfter5)
                //     // get apparent size of overlay
                //     const apparentSize = pop_overlay.getElement().getBoundingClientRect()
                //     console.log('Apparent size of overlay:', apparentSize)
                //     // set positioning centre centre
                //     pop_overlay.setPositioning('center-center')
                //   }
                // }, 3000)

                // translate the pop_overlay by -100% of height
                const heightOfPopup = pop_container.clientHeight
                const widthOfPopup = pop_container.clientWidth
                console.log('Width of popup:', widthOfPopup)
                // Set the width of the popup as a proportion of the map width
                const mapSize = map.getSize()
                if (!mapSize) {
                    return
                }
                updatePopupWidth(mapSize[0])

                // shift slightly to the left to account for the popup arrow position,
                // and shift up by the height of the arrow
                // pop_overlay.setOffset([-48, -heightOfPopup - 10])
                // pop_overlay.setOffset([-48, -heightOfPopup - 10])

                const coordsFormat1 = coords.map((coord) => coord.toFixed(0)).join(', ')

                // coordinates style - seperate little grey box
                const coordsStyle =
                    'background-color: rgba(200, 200, 200, 0.5); padding: 5px; border-radius: 5px;'
                // const addStyle => (message: string) => {
                //   return `<span style="${coordsStyle}">${message}</span>`
                // }
                // Add the coordinates to the popup content
                // pop_content.innerHTML = `<p>Coordinates: ${coordsFormat2}</p>`
                // With style:

                pop_content.innerHTML = `<h3>Time Series - ${popupDataset.name}</h3>`

                pop_content.innerHTML += `<p>Map Coordinates (EPSG:3857): <span style="${coordsStyle}">[${coordsFormat1}] </span></p>`
                // Add the coordinates in lat/lon
                const lonLat = toLonLat(coords)
                const clickLon = Number(lonLat[0])
                const clickLat = Number(lonLat[1])
                const coordsFormat2 = [clickLon, clickLat].map((coord) => coord.toFixed(4)).join(', ')
                // Flip xy so it's lat, lon
                const lat = coordsFormat2.split(',')[1]
                const lon = coordsFormat2.split(',')[0]
                pop_content.innerHTML += `<p>Geographic Coordinates (EPSG:4326): <span style="${coordsStyle}">[${lat}, ${lon}]</span></p>`
                pop_content.innerHTML += `<p>Scatterer ID: <span style="${coordsStyle}">LOADING_SCATTERER_ID</span></p>`
                pop_content.innerHTML += `<p>Time Series ID: <span style="${coordsStyle}">LOADING_TIME_SERIES_ID</span></p>`
                // Add displacement rate
                pop_content.innerHTML += `<p>Model displacement rate: <span style="${coordsStyle}"> LOADING_VELOCITY mm/year</span></p>`
                pop_content.innerHTML += `<p>Coherence: <span style="${coordsStyle}">LOADING_COHERENCE</span> <span class="LOADING_COHERENCE_ASSESSMENT_CLASS">LOADING_COHERENCE_ASSESSMENT</span></p>`
                pop_content.innerHTML += '<canvas id="time-series-chart"></canvas>'
                pop_content.innerHTML +=
                    '<button id="copy-timeseries-btn" style="margin-top: 10px; padding: 5px 10px; background-color: #007bff; color: white; border: none; border-radius: 3px; cursor: pointer;">Copy Data to Clipboard</button>'
                // Add some dummy dates and values for the chart
                let dates: string[] = []

                // set the popup container display to inherit
                pop_container.style.display = 'inherit'
                pop_content.style.width = `${Math.round(baseWidth.value)}px`
                pop_content.style.maxWidth = `${Math.round(baseWidth.value)}px`

                try {
                    // Fetch dataset date-series and nearest scatterer in parallel using full-precision click coordinates.
                    const [datasetResponse, nearestResponse] = await Promise.all([
                        fetch(`/api/psi-datasets/${popupDataset.id}/`),
                        fetch(
                            `/api/nearest-scatterer/?dataset=${popupDataset.id}&lat=${encodeURIComponent(String(clickLat))}&lon=${encodeURIComponent(String(clickLon))}&coherence_min=${encodeURIComponent(String(cohThreshold.value))}`
                        )
                    ])

                    if (!datasetResponse.ok) {
                        throw new Error(`Dataset HTTP error! status: ${datasetResponse.status}`)
                    }
                    if (nearestResponse.status === 404) {
                        pop_content.innerHTML += `<p>No nearby scatterer meets coherence >= ${cohThreshold.value.toFixed(2)} for this click.</p>`
                        return
                    }
                    if (!nearestResponse.ok) {
                        throw new Error(`Nearest HTTP error! status: ${nearestResponse.status}`)
                    }

                    const datasetInfo = await datasetResponse.json()
                    dates = Array.isArray(datasetInfo?.date_series)
                        ? datasetInfo.date_series.filter(
                              (dateValue: unknown): dateValue is string => typeof dateValue === 'string'
                          )
                        : []

                    const nearestScatterer = await nearestResponse.json()
                        console.log(nearestScatterer)

                        const scattererAnchorCoordinate = getScattererAnchorCoordinate(
                            nearestScatterer?.location
                        )
                        if (scattererAnchorCoordinate) {
                            popupAnchorCoordinate = scattererAnchorCoordinate
                            updatePopupLeaderLine()
                        }

                        // Use the id to get the time series data
                        const scattererId = nearestScatterer.time_series
                        const nearestScattererId = nearestScatterer?.id ?? nearestScatterer?.scatterer_id ?? 'unknown'
                        const nearestTimeSeriesId = nearestScatterer?.time_series ?? 'unknown'

                        pop_content.innerHTML = pop_content.innerHTML.replace(
                            'LOADING_SCATTERER_ID',
                            String(nearestScattererId)
                        )
                        pop_content.innerHTML = pop_content.innerHTML.replace(
                            'LOADING_TIME_SERIES_ID',
                            String(nearestTimeSeriesId)
                        )

                        const timeSeriesResponse = await fetch(
                            `/api/psi-scatterer-timeseries/?dataset=${popupDataset.id}&id=${scattererId}`
                        )
                        if (!timeSeriesResponse.ok) {
                            throw new Error(`Time-series HTTP error! status: ${timeSeriesResponse.status}`)
                        }
                        const dataJson = await timeSeriesResponse.json()
                                console.log('dataJson.values:', dataJson.values)
                                console.log('SELECTED POINT')
                                console.log('nearestScatterer:', nearestScatterer)
                                selectedPoint.value = nearestScatterer
                                var vel_3_sig_fig = (nearestScatterer.velocity * 1000).toFixed(2)
                                pop_content.innerHTML = pop_content.innerHTML.replace(
                                    'LOADING_COHERENCE_ASSESSMENT_CLASS',
                                    getCoherenceAssessmentClass(nearestScatterer.coherence)
                                )
                                    pop_content.innerHTML = pop_content.innerHTML.replace(
                                        'LOADING_COHERENCE_ASSESSMENT',
                                        getCoherenceAssessment(nearestScatterer.coherence)
                                    )
                                    var coh_3_sig_fig = nearestScatterer.coherence.toFixed(3)
                                    pop_content.innerHTML = pop_content.innerHTML.replace(
                                        'LOADING_COHERENCE',
                                        coh_3_sig_fig
                                    )
                                    pop_content.innerHTML = pop_content.innerHTML.replace(
                                        'LOADING_VELOCITY',
                                        vel_3_sig_fig
                                    )

                                selectedPoint.value = nearestScatterer

                                const featureVelocity = getFeatureNumberProperty(clickedFeature, 'velocity')
                                const featureCoherence = getFeatureNumberProperty(clickedFeature, 'coherence')
                                const featureLon = getFeatureNumberProperty(clickedFeature, 'source_lon')
                                const featureLat = getFeatureNumberProperty(clickedFeature, 'source_lat')
                                const nearestVelocity = Number(nearestScatterer?.velocity)
                                const nearestCoherence = Number(nearestScatterer?.coherence)

                                if (debugClickConsistency.value) {
                                    console.group('[PSI click debug]')
                                    console.log('dataset from clicked layer:', clickedDatasetId)
                                    console.log('dataset resolved for popup:', popupDataset.id)
                                    console.log('click EPSG:4326', {
                                        lon: clickLon,
                                        lat: clickLat
                                    })
                                    console.log('clicked feature properties', {
                                        scattererId: getFeatureProperty(clickedFeature, 'scatterer_id'),
                                        lon: featureLon,
                                        lat: featureLat,
                                        velocity: featureVelocity,
                                        coherence: featureCoherence
                                    })
                                    console.log('nearest scatterer payload', nearestScatterer)
                                    console.log('consistency deltas', {
                                        deltaVelocityMmYr:
                                            Number.isFinite(featureVelocity) && Number.isFinite(nearestVelocity)
                                                ? (nearestVelocity - featureVelocity) * 1000
                                                : null,
                                        deltaCoherence:
                                            Number.isFinite(featureCoherence) && Number.isFinite(nearestCoherence)
                                                ? nearestCoherence - featureCoherence
                                                : null,
                                        dateCount: dates.length,
                                        valuesCount: Array.isArray(dataJson?.values)
                                            ? dataJson.values.length
                                            : null
                                    })
                                    console.groupEnd()
                                }

                                // Create a time series chart
                                const formattedData = dates.reduce<Record<string, number>>((acc, date, index) => {
                                    acc[date] = Number(dataJson.values[index]) * 1000
                                    return acc
                                }, {})

                                const valuesCount = Array.isArray(dataJson?.values)
                                    ? dataJson.values.length
                                    : 0
                                if (dates.length !== valuesCount) {
                                    console.warn(
                                        `[PSI click debug] date/value length mismatch for dataset ${popupDataset.id}: dates=${dates.length}, values=${valuesCount}`
                                    )
                                }

                                // Convert this into a chart.js time series chart
                                // make some data with 'labels', 'datasets'
                                const labels = Object.keys(formattedData)
                                const data = {
                                    labels: labels,
                                    datasets: [
                                        {
                                            label: 'Displacement Time Series (mm)',
                                            data: Object.values(formattedData),
                                            fill: false,
                                            borderColor: 'rgb(75, 192, 192)',
                                            tension: 0.1
                                        }
                                    ]
                                }
                                // get the canvas
                                const chartbox = document.getElementById(
                                    'time-series-chart'
                                ) as HTMLCanvasElement
                                if (chartbox === null) {
                                    throw new Error('chartbox is null')
                                }

                                // y axis label
                                const yaxis = document.createElement('div')
                                yaxis.innerHTML = 'Displacement (mm)'
                                yaxis.style.position = 'absolute'
                                yaxis.style.top = '10px'
                                yaxis.style.left = '10px'

                                // To control the size of the chart we need to set the width of the canvas / parent div
                                console.log('chartbox:', chartbox)
                                console.log('chartbox.style.width:', chartbox.style.width)
                                console.log('baseWidth.value:', baseWidth.value)
                                // set the canvas width
                                console.log('Setting chart width to: ', baseWidth.value)
                                chartbox.innerHTML = ' '
                                chartbox.width = baseWidth.value
                                chartbox.style.width = `${Math.round(baseWidth.value)}px`
                                chartbox.style.maxWidth = `${Math.round(baseWidth.value)}px`
                                chartbox.style.display = 'block'
                                chartbox.style.backgroundColor = '#ffffff'
                                chartbox.style.borderRadius = '8px'
                                isChartOverlayOpen.value = true

                                // Get the canvas
                                new Chart(chartbox, {
                                    type: 'line',
                                    data: data,
                                    options: {
                                        responsive: true,
                                        scales: {
                                            y: {
                                                title: {
                                                    // Add this section
                                                    display: true, // Make sure the title is displayed
                                                    text: 'Displacement (mm)', // Set the title text
                                                    color: 'black', // Optional: Set the color of the title
                                                    font: {
                                                        // Optional: Customize font properties
                                                        size: 16, // Font size
                                                        family: 'Arial' // Font family
                                                    }
                                                }
                                            }
                                        }
                                    }
                                })

                                // Set position to middle of map container
                                const mapSizeForCenter = map.getSize()
                                if (!mapSizeForCenter) {
                                    return
                                }
                                const middleOfMapCoords = map.getCoordinateFromPixel([
                                    mapSizeForCenter[0] / 2,
                                    mapSizeForCenter[1] / 2
                                ])
                                pop_overlay.setPosition(middleOfMapCoords)
                                updatePopupLeaderLine()
                                // Reset the positioning now content is loaded
                                pop_overlay.setPositioning('center-center')
                                // Set size
                                updatePopupWidth(mapSizeForCenter[0])
                                updatePopupLeaderLine()
                                // check if the pop_container has style
                                if (!pop_container.style.background) {
                                    pop_container.style.backgroundColor = 'beige'
                                }

                                // Add event listener for copy button
                                const copyBtn = document.getElementById('copy-timeseries-btn')
                                if (copyBtn) {
                                    copyBtn.addEventListener('click', () => {
                                        const latNum = parseFloat(lat)
                                        const lonNum = parseFloat(lon)
                                        const valuesArray = Object.values(formattedData) as number[]
                                        copyTimeSeriesData(dates, valuesArray, latNum, lonNum)
                                    })
                                }
                } catch (error) {
                    console.error('Error building click popup data:', error)
                }
            })
            // Add the overlay to the map
            map.addOverlay(pop_overlay)

            // Add a vue watch for cohThreshold
            watch(cohThreshold, (newVal) => {
                console.log('Coh threshold changed:', newVal)
                if (newVal !== initialControlValues.value.cohThreshold) {
                    controlsChanged.value = true
                }
            }) // watch

            // Add a vue watch for pointSize
            watch(pointSize, (newVal) => {
                console.log('Point size changed:', newVal)
                if (newVal !== initialControlValues.value.pointSize) {
                    controlsChanged.value = true
                }
            }) // watch
            
            // Add a vue watch for velThreshold
            watch(velThreshold, (newVal) => {
                console.log('Velocity threshold changed:', newVal)
                if (newVal !== initialControlValues.value.velThreshold) {
                    controlsChanged.value = true
                }
            }) // watch

            // Add a vue watch for maxConcurrentRequests
            watch(maxConcurrentRequests, (newVal) => {
                console.log('Max concurrent requests changed:', newVal)
                if (newVal !== initialControlValues.value.maxConcurrentRequests) {
                    controlsChanged.value = true
                }
            }) // watch
            
            // Add a vue watch for selectedCategory
            watch(selectedCategory, (newVal) => {
                console.log('Selected category changed:', newVal)
                if (newVal !== initialControlValues.value.selectedCategory) {
                    controlsChanged.value = true
                }
            }) // watch
        }) // onMounted

        onBeforeUnmount(() => {
            const ids: number[] = Array.from(layerRegistry.value.keys())
            ids.forEach((datasetId) => removeLayerForDataset(datasetId))
            removePopupDragListeners?.()
            if (mapStore.map && popupLeaderLayer) {
                mapStore.map.removeLayer(popupLeaderLayer as any)
            }
        })

        // Computed property for formatted velocity threshold display
        const formattedVelThreshold = computed(() => {
            return (velThreshold.value * 1000).toFixed(1)
        })

        return {
            siteId,
            datasetsAdapter,
            sortedDatasets,
            datasetSections,
            enableMultipleLayers,
            selectedDataset,
            selectedDatasetIds,
            getDatasetDisplayName,
            getDatasetLayerStatus,
            getDatasetLayerStatusLabel,
            getDatasetLayerStatusClass,
            unloadSelectedLayers,
            cohThreshold,
            pointSize,
            velThreshold,
            maxConcurrentRequests,
            formattedVelThreshold,
            handleDatasetChange,
            toggleDatasetSelection,
            findFeaturesForDataset,
            selectedPoint,
            drawer,
            selectedCategory,
            categories,
            set_preset,
            isLoading,
            isAnyLayerLoading,
            loadingProgress,
            totalFeatures,
            copyTimeSeriesData,
            goBackToSite,
            toggleAOI,
            aoiLayerVisible,
            showLegendOverlay,
            legendGradientStyle,
            toggleLegendOverlay,
            controlsChanged,
            reloadWithNewControls
        }
    }
})
</script>

<template>
    <div class="flex">
        <div class="w-1over3">
            <div class="flex items-center gap-3 mb-2">
                <Button
                    @click="goBackToSite"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    outlined
                    size="small"
                    class="flex-shrink-0"
                    aria-label="Back to Site"
                ></Button>
                <h1 class="m-0">{{ siteId }} PSI Datasets</h1>
            </div>
            <!-- Table for available point layers -->
            <Fieldset legend="Deformation Datasets" :toggleable="true" :collapsed="false">
                <div class="layer-mode-toggle">
                    <label for="multi-layer-toggle">Enable multiple layers</label>
                    <input
                        id="multi-layer-toggle"
                        type="checkbox"
                        v-model="enableMultipleLayers"
                        :disabled="isAnyLayerLoading"
                    />
                </div>
                <div v-if="datasetSections.length > 0" class="dataset-sections-scroll">
                    <Fieldset
                        v-for="section in datasetSections"
                        :key="section.key"
                        :legend="section.title"
                        :toggleable="true"
                        :collapsed="false"
                        class="dataset-section"
                    >
                        <table class="dataset-grid">
                            <thead>
                                <tr>
                                    <th>Select</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(dataset, index) in section.datasets" :key="`${section.key}-${dataset.id ?? index}`">
                                    <template v-if="dataset.id !== null">
                                      <td>
                                        <input
                                                                                :type="enableMultipleLayers ? 'checkbox' : 'radio'"
                                            :id="String(dataset.id)"
                                            name="pointLayer"
                                            :value="dataset.name"
                                                                                :checked="enableMultipleLayers ? selectedDatasetIds.includes(Number(dataset.id)) : selectedDataset?.id === dataset.id"
                                                                                :disabled="isAnyLayerLoading"
                                                                                @change="(event) => {
                                                                                    const target = event.target as HTMLInputElement
                                                                                    if (enableMultipleLayers) {
                                                                                        toggleDatasetSelection(dataset, target.checked)
                                                                                    } else {
                                                                                        handleDatasetChange(dataset)
                                                                                    }
                                                                                }"
                                        />
                                                                    </td>
                                                                    <td>
                                        <label :for="dataset.name">{{ getDatasetDisplayName(dataset) }}</label>
                                                                    </td>
                                                                    <td>
                                                                        <span class="layer-status" :class="getDatasetLayerStatusClass(dataset.id)">
                                                                            {{ getDatasetLayerStatusLabel(dataset.id) }}
                                                                        </span>
                                                                    </td>
                                                                </template>
                                                                <td v-else colspan="3">Error with dataset id</td>
                                </tr>
                            </tbody>
                        </table>
                    </Fieldset>
                </div>
                <div v-else class="dataset-empty-state">
                    No PSI datasets available for this site yet.
                </div>
                <!-- Load button -->
                                <div class="dataset-actions">
                                    <Button @click="findFeaturesForDataset" :disabled="isAnyLayerLoading">Load</Button>
                                    <Button @click="unloadSelectedLayers" :disabled="isAnyLayerLoading" severity="secondary" outlined>
                                        Unload Selected
                                    </Button>
                                </div>

                <!-- Loading progress bar -->
                <div v-if="isLoading" class="mt-4">
                    <ProgressBar
                        :value="loadingProgress"
                        :showValue="true"
                        class="custom-progress"
                        style="width: 100%"
                    />
                    <div class="progress-text">
                        Loading points: {{ Math.round(loadingProgress) }}%
                    </div>
                </div>
            </Fieldset>

            <Fieldset legend="View Controls" :toggleable="true" class="view-controls-container" :class="{ 'controls-disabled': isAnyLayerLoading }">
                <!-- Reload button for when controls change -->
                <Button 
                    v-if="controlsChanged"
                    @click="reloadWithNewControls"
                    :disabled="isAnyLayerLoading"
                    class="reload-button"
                    severity="warning"
                    size="small"
                    icon="pi pi-refresh"
                    v-tooltip="'Reload to see changes'"
                >
                    RELOAD
                </Button>
                
                <h3>Use Case Presets:</h3>
                <div class="card">
                    <div class="presets-container">
                        <div
                            v-for="category in categories"
                            :key="category.key"
                            class="preset-option"
                        >
                            <RadioButton
                                v-model="selectedCategory"
                                :id="category.key"
                                :value="category.key"
                                @change="
                                    () => {
                                        set_preset()
                                    }
                                "
                            />
                            <!-- <input type="radio" :id="category.key" name="category" :value="category.key" /> -->
                            <label :for="category.key">{{ category.name }}</label>
                        </div>
                    </div>
                </div>
                <!-- Table for view controls -->
                <h3>Controls</h3>
                <table class="controls-table">
                    <!-- <tr>
            <th>Control</th>
            <th>Value</th>
          </tr> -->
                    <!-- Dropdown for colour ramp preset -->
                    <tbody>
                    <tr>
                        <td>
                            Colour Ramp<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Colour Ramp', [
                                        'This controls the color mapping of the point cloud data'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <select id="colour-ramp" name="colour-ramp">
                                <option value="RGB">RGB</option>
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Point Size<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Point Size', [
                                        'This is the size of the points rendered on the map'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="point-size"
                                name="point-size"
                                min="1"
                                max="10"
                                step="0.1"
                                value="3"
                                v-model.number="pointSize"
                            /><br />
                            <span class="slider-label">{{ pointSize }} pixels</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Coherence Threshold<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Coherence Threshold', [
                                        `Coherence in InSAR (Interferometric Synthetic Aperture Radar) data processing is a measure of how similar the radar signals are between two different observations of the same area. It
        ranges from 0 (completely different signals) to 1 (perfectly identical signals).`,
                                        `
        For deformation monitoring, coherence tells us how reliable the phase difference
        (interferogram) is for measuring ground displacement. High coherence means that the surface
        properties haven t changed much between radar acquisitions, so we can confidently extract
        deformation information. Low coherence, on the other hand, indicates changes due to
        vegetation growth, land use changes, or atmospheric noise, making deformation measurements
        less reliable.`
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="coherence-threshold"
                                name="coherence-threshold"
                                min="0"
                                max="1"
                                step="0.01"
                                value="0.7"
                                v-model="cohThreshold"
                            /><br />

                            <span class="slider-label">{{ cohThreshold }}</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Velocity Colour Scale<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Velocity Colour Scale', [
                                        `
              This control sets the velocity range used by the map colour scale, in millimetres per year.
              For example, a low value such as 1 mm/year will push any larger uplift or subsidence rates to the extreme ends of the scale.
              The scale is symmetric around zero, so a value of 1 mm/year means the legend spans from -1 mm/year to +1 mm/year.
              `
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="velocity-ramp"
                                name="velocity-ramp"
                                min="0"
                                max="0.01"
                                step="0.0001"
                                v-model="velThreshold"
                            /><br />

                            <span class="slider-label">{{ formattedVelThreshold }} mm/year</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Request Concurrency<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Request Concurrency', [
                                        'Limits how many scatterer data requests can be in flight at once.',
                                        'Lower values reduce load on the API server but may increase total load time.',
                                        'A value around 10 is a sensible default for avoiding request spikes.'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="request-concurrency"
                                name="request-concurrency"
                                min="1"
                                max="20"
                                step="1"
                                v-model.number="maxConcurrentRequests"
                            /><br />
                            <span class="slider-label">{{ maxConcurrentRequests }} requests</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Date Range<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Date Range', [
                                        'This feature allows filtering of time series data by date range.',
                                        'Currently disabled - this functionality will be implemented in an upcoming release.',
                                        'When active, you will be able to select start and end dates to filter the PSI data displayed on the map.'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td v-tooltip="'This feature is currently disabled and will be implemented in an upcoming release. When active, you will be able to select start and end dates to filter the PSI data displayed on the map.'">
                            <div 
                                class="date-range-container"
                            >
                                <input type="date" id="date-range" name="date-range" disabled />
                                <br />to<br />
                                <input type="date" id="date-range" name="date-range" disabled />
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </Fieldset>
        </div>
        <div class="w-2over3 map-panel">
            <MapComponent>
                <template #customControls>
                    <Button
                        :label="aoiLayerVisible ? 'Hide AOI' : 'Show AOI'"
                        icon="pi pi-map"
                        size="small"
                        :outlined="!aoiLayerVisible"
                        severity="secondary"
                        @click="toggleAOI"
                    />
                    <Button
                        :label="showLegendOverlay ? 'Hide legend' : 'Show legend'"
                        icon="pi pi-palette"
                        size="small"
                        :outlined="!showLegendOverlay"
                        severity="secondary"
                        @click="toggleLegendOverlay"
                    />
                </template>
                <MapNotesLayer :site-id="siteId" />
            </MapComponent>
            <div v-if="showLegendOverlay" class="legend-overlay">
                <div class="legend-title">Velocity Colour Scale</div>
                <div class="legend-subtitle">Positive values indicate uplift or movement towards the sensor, negative values indicate subsidence or movement away from the sensor.</div>
                <div class="legend-gradient" :style="legendGradientStyle"></div>
                <div class="legend-scale">
                    <span>-{{ formattedVelThreshold }} mm/year</span>
                    <span>0</span>
                    <span>{{ formattedVelThreshold }} mm/year</span>
                </div>
            </div>
        </div>
    </div>
    <!-- container -->
    <div id="popup" class="ol-popup">
        <a href="#" id="popup-closer" class="ol-popup-closer"></a>
        <div id="popup-content"></div>
    </div>
</template>

<style scoped>
.flex {
    display: flex;
}

.w-1over3 {
    width: 33.33%;
}

.w-2over3 {
    width: 66.67%;
}

.map-panel {
    position: relative;
}

.legend-overlay {
    position: absolute;
    top: 4.25rem;
    right: 1rem;
    z-index: 19;
    width: 280px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.18);
    padding: 0.9rem;
    color: #0f172a;
}

.legend-title {
    font-weight: 700;
    margin-bottom: 0.35rem;
}

.legend-subtitle {
    font-size: 0.82rem;
    line-height: 1.4;
    color: #475569;
    margin-bottom: 0.75rem;
}

.legend-gradient {
    height: 18px;
    border-radius: 999px;
    border: 1px solid #cbd5e1;
}

.legend-scale {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.35rem;
    font-size: 0.8rem;
    color: #475569;
}

#false-color-composite-list {
    display: none;
}

#page-header {
    text-align: center;
    color: var(--color-text);
    font-size: 24px;
}

/* Add a global class that forces the grid layour for container */
.site-hub-container {
    display: grid;
    grid-template-columns: 1fr 3fr;
    /* Two columns, adjust the width as needed */
}

/* Controls on the left */
.controls {
    /* background-color: #f0f0f0;  */
    /* Background color for the left column */
    padding: 20px;
    /* Add padding for spacing */
}

.control-box {
    border: var(--color-text) 1px double;
    padding: 10px;
}

/* Map container on the right */
.map-container {
    /* You can adjust the size of the map container here */
    width: 100%;
    height: 400px;
    /* Adjust the height as needed */
    position: relative;
    /* Ensure relative positioning for children elements */
}

/* Style map buttons as needed */
.map-container button {
    margin-top: 10px;
    /* Add margin between buttons and the map */
}

/* Style the links in the control panel as buttons */
.vertical-controls a {
    display: flex;
    flex-direction: column;
    padding: 10px;
    margin: 5px;
    border: 1px solid var(--color-border);
    border-radius: 5px;
    text-decoration: none;
    color: var(--color-text);
    background-color: var(--color-background);
}

.vertical-controls a {
    flex: 1;
    text-align: center;
}

/* Hover background */
.vertical-controls a:hover {
    background-color: var(--color-background-mute);
}

.horizontal-controls {
    display: flex;
    flex-direction: row;
    padding: 10px;
    justify-content: space-evenly;
}

.no-border {
    border: none;
}

.site-data-item {
    background-color: var(--color-background-mute);
    padding: 5px;
    margin: 5px;
    border: 1px solid var(--brand-color);
    border-radius: 5px;
    text-decoration: none;
    color: #000;
}

.site-data-item:hover {
    background-color: var(--color-background);
}

.ol-overlay-container {
    background: beige;
}

.ol-popup {
    background: #ffffff !important;
    padding: 12px;
    border-radius: 10px;
}

#popup-content {
    box-sizing: border-box;
}

:deep(.coherence-assessment) {
    display: block;
    width: 100%;
    box-sizing: border-box;
    margin-top: 0.4rem;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    border: 1px solid transparent;
    color: #475569;
    font-style: italic;
    font-weight: 600;
    line-height: 1.35;
    white-space: normal;
    overflow-wrap: anywhere;
}

:deep(.coherence-assessment.is-reliable) {
    color: #166534;
    background: #dcfce7;
    border-color: #86efac;
}

:deep(.coherence-assessment.is-caution) {
    color: #9a3412;
    background: #ffedd5;
    border-color: #fdba74;
}

:deep(.coherence-assessment.is-noisy) {
    color: #991b1b;
    background: #fee2e2;
    border-color: #fca5a5;
}

.ol-popup-closer {
    text-decoration: none;
    position: absolute;
    top: 2px;
    right: 8px;
}

.ol-popup-closer:after {
    content: '\2716';
}

.custom-progress :deep(.p-progressbar) {
    height: 1.5rem;
    background: #f0f0f0;
}

.custom-progress :deep(.p-progressbar-value) {
    background: #4caf50;
    transition: width 0.2s ease;
}

.custom-progress :deep(.p-progressbar-label) {
    color: #000;
    font-weight: bold;
}

.progress-text {
    text-align: center;
    font-size: 0.875rem;
    margin-top: 0.25rem;
    font-weight: 600;
}

.help-button {
    margin-right: 12px;
    font-size: 0.75rem;
    padding: 2px 8px;
    min-width: 16px;
    height: 16px;
    text-transform: lowercase;
}

.slider-label {
    display: inline-block;
    min-width: 3rem;
    text-align: left;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    margin-left: 8px;
    background: ghostwhite;
    border: 1px solid gainsboro;
    border-radius: 4px;
    padding: 2px 6px;
}

/* Container query for responsive controls */
.view-controls-container {
    container-type: inline-size;
    position: relative;
}

.controls-disabled {
    opacity: 0.6;
    pointer-events: none;
}

.dataset-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
}

.dataset-grid {
    width: 100%;
    border-collapse: collapse;
}

.dataset-sections-scroll {
    max-height: 66vh;
    overflow-y: auto;
    overflow-x: hidden;
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-right: 0.25rem;
}

.dataset-grid th,
.dataset-grid td {
    padding: 6px 8px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
}

.dataset-grid th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
}

.dataset-section {
    background: #fff;
    border-radius: 8px;
}

.dataset-empty-state {
    margin-bottom: 8px;
    padding: 0.75rem;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    color: #475569;
    background: #f8fafc;
}

.layer-mode-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.layer-status {
    margin-left: 8px;
    font-size: 0.75rem;
    border-radius: 999px;
    padding: 2px 8px;
    border: 1px solid transparent;
    text-transform: capitalize;
    display: inline-block;
    white-space: normal;
    line-height: 1.2;
}

.status-idle {
    background: #f3f4f6;
    color: #374151;
    border-color: #d1d5db;
}

.status-loading {
    background: #dbeafe;
    color: #1d4ed8;
    border-color: #93c5fd;
}

.status-loaded {
    background: #dcfce7;
    color: #166534;
    border-color: #86efac;
}

.status-skipped {
    background: #ffedd5;
    color: #9a3412;
    border-color: #fdba74;
}

.status-present {
    background: #e0f2fe;
    color: #075985;
    border-color: #7dd3fc;
}

.status-error {
    background: #fee2e2;
    color: #991b1b;
    border-color: #fca5a5;
}

.view-controls-container h3 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0.5rem 0;
}

.reload-button {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 10;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

.presets-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: flex-start;
}

.preset-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 auto;
    min-width: 0; /* Allow shrinking */
}

.preset-option label {
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2;
    font-size: 0.9rem;
}

/* Make presets stack vertically on narrow containers */
@container (max-width: 500px) {
    .presets-container {
        flex-direction: column;
        gap: 0.5rem;
    }
    
    .preset-option {
        width: 100%;
        justify-content: flex-start;
    }
}

.controls-table {
    width: 100%;
    table-layout: fixed;
}

.controls-table td {
    padding: 8px 4px;
    vertical-align: top;
}

.controls-table td:first-child {
    width: 40%;
}

.controls-table td:last-child {
    width: 60%;
}

/* Switch to stacked layout for narrow containers */
@container (max-width: 400px) {
    .controls-table,
    .controls-table tbody,
    .controls-table tr,
    .controls-table td {
        display: block;
        width: 100%;
    }
    
    .controls-table tr {
        border-bottom: 1px solid #e0e0e0;
        margin-bottom: 1rem;
        padding-bottom: 1rem;
    }
    
    .controls-table td {
        padding: 4px 0;
        border: none;
    }
    
    .controls-table td:first-child {
        font-weight: 600;
        margin-bottom: 0.5rem;
    }
    
    .controls-table input[type="range"] {
        width: 100%;
        max-width: 100%;
    }
    
    .controls-table select {
        width: 100%;
        max-width: 100%;
    }
}
</style>
